// "2h 30m" reads faster at a counter than "150 minutes".
export function formatMinutes(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return '—';
  if (minutes <= 0) return '0m';
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest}m`;
  if (rest === 0) return `${hours}h`;
  return `${hours}h ${rest}m`;
}

// The live countdown ticks in seconds, so it needs its own format: under an
// hour staff want to see mm:ss, above it h:mm:ss is enough.
export function formatCountdown(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;
  const pad = (value: number) => String(value).padStart(2, '0');
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${pad(minutes)}:${pad(seconds)}`;
}

// A wallet balance, honest about the seconds. Billing is exact to the second
// across sessions, so a customer who stopped at 59:27 really does have 59:27 —
// showing "1h" here is what made the card disagree with the station's own
// countdown and with what the next session starts from.
//
// The seconds are only shown when there are any, so the common case of a freshly
// sold pack still reads as a clean "2h" rather than "2h 0s".
export function formatBalance(totalSeconds: number | null | undefined): string {
  if (totalSeconds === null || totalSeconds === undefined) return '—';
  const safe = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  if (seconds === 0) return formatMinutes(minutes);
  if (minutes === 0) return `${seconds}s`;
  return `${formatMinutes(minutes)} ${seconds}s`;
}

export function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString([], {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString([], {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function formatMoney(amount: string | number | null | undefined): string {
  if (amount === null || amount === undefined) return '₹0';
  const value = typeof amount === 'string' ? Number(amount) : amount;
  if (Number.isNaN(value)) return '₹0';
  return `₹${value.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

export function elapsedSince(iso: string): string {
  const minutes = Math.max(
    0,
    Math.floor((Date.now() - new Date(iso).getTime()) / 60000),
  );
  return formatMinutes(minutes);
}

// "5m ago" for list rows; past a week the date itself is more useful.
export function formatAgo(iso: string | null | undefined): string {
  if (!iso) return '—';
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (minutes < 24 * 60) return `${Math.floor(minutes / 60)}h ago`;
  if (minutes < 7 * 24 * 60) return `${Math.floor(minutes / (24 * 60))}d ago`;
  return formatDate(iso);
}

export const POOL_LABEL: Record<'general' | 'simulator', string> = {
  general: 'PC gaming',
  simulator: 'Driving simulator',
};

// What paid for a session, for history rows. PC sessions name their pool.
export const SOURCE_LABEL: Record<'general' | 'simulator' | 'ps5_session', string> = {
  ...POOL_LABEL,
  ps5_session: 'PS5',
};

// Mid-sentence form. These cannot be derived by lowercasing POOL_LABEL — that
// turns "PC gaming" into "pc gaming". Wording matches the backend's own error
// copy, so a refusal and the recovery it prompts read as the same thing.
export const POOL_LABEL_INLINE: Record<'general' | 'simulator', string> = {
  general: 'PC gaming',
  simulator: 'driving simulator',
};

export const STATION_TYPE_LABEL: Record<string, string> = {
  pc_standard: 'Gaming PC',
  pc_simulator: 'Simulator',
  ps5: 'PS5',
};

// Which wallet pool a station's session draws from. The pools never mix, so
// this drives both the "can they sit here?" check and which pack to preselect
// when staff sell a recharge from a failed start.
export function poolForStationType(
  stationType: string,
): 'general' | 'simulator' | null {
  if (stationType === 'pc_standard') return 'general';
  if (stationType === 'pc_simulator') return 'simulator';
  return null;
}

// What a customer still has to hand over: unpaid tab lines less the credit
// already held for them. A part-payment by amount leaves its remainder as
// credit against the lines, so the line total alone overstates the debt.
export function owedAfterCredit(dueAmount: number, creditBalance: number): number {
  return Math.max(0, Math.round((dueAmount - creditBalance) * 100) / 100);
}

// Credit stays on the account until a payment spends it, so it is shown in
// full for as long as it exists — adding lines to the tab does not use it up.
// With lines open, staff also see what will be left once the tab is settled.
export function creditLabel(creditBalance: number, dueAmount: number): string {
  const label = `Credit ${formatMoney(creditBalance)}`;
  if (!(dueAmount > 0)) return label;
  const left = Math.max(0, Math.round((creditBalance - dueAmount) * 100) / 100);
  return left > 0
    ? `${label} · ${formatMoney(left)} left after tab`
    : `${label} · all used by tab`;
}

// Lifetime play summed across platforms, for the customer card's "in all".
export function totalPlayedSeconds(
  totals: Partial<Record<string, { seconds_played: number }>>,
): number {
  return Object.values(totals).reduce((sum, total) => sum + (total?.seconds_played ?? 0), 0);
}
